import {
  IWorkingStepsAPI,
  WorkingStepsResponse,
} from 'controllers/IWorkingStepsAPI'
import {
  AdditionalWorkingStepDto,
  BoMItemIdAndActivityId,
  CopyWorkingStepArgs,
  InsertWorkingStepsArgs,
  UpdateWorkingStepProductionOrderResponse,
  UpdateWorkingStepsArgs,
  UpdateWorkingStepsProductionOrderArgs,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { WorkingStepsController } from '../../../controllers/WorkingStepsController'
import { OnRequestChangeCallback } from '../BaseAPI'
import { InternalAPI } from './InternalAPI'

export class WorkingStepsAPI extends InternalAPI implements IWorkingStepsAPI {
  constructor(
    organizationId: string,
    private onRequestChange: OnRequestChangeCallback,
  ) {
    super()
    this.baseAddress += `/api/parties/${organizationId}/projects`
  }

  async GetWorkingSteps(projectId: string, rowId: string) {
    return await this.GetAsync<WorkingStepsResponse>({
      id: WorkingStepsController.WorkingStepsOperations
        .GetAvailableWorkingSteps,
      relativePath: `/${projectId}/items/${rowId}/workingsteps`,
      onRequestChange: this.onRequestChange,
    })
  }

  async InsertWorkingSteps(projectId: string, args: InsertWorkingStepsArgs) {
    return await this.PostAsync<void>({
      id: WorkingStepsController.WorkingStepsOperations.SAVE_DATA,
      relativePath: `/${projectId}/workingsteps/insert`,
      data: args,
      onRequestChange: this.onRequestChange,
    })
  }

  async UpdateWorkingSteps(projectId: string, args: UpdateWorkingStepsArgs) {
    return await this.PostAsync<void>({
      id: WorkingStepsController.WorkingStepsOperations.SAVE_DATA,
      relativePath: `/${projectId}/workingsteps/update`,
      data: args,
      onRequestChange: this.onRequestChange,
    })
  }

  public async DeleteWorkingSteps(
    projectId: string,
    boMItemAndActivityIds: BoMItemIdAndActivityId[],
  ) {
    return await this.PostAsync<void>({
      id: WorkingStepsController.WorkingStepsOperations.DELETE_DATA,
      relativePath: `/${projectId}/workingsteps/delete`,
      data: {
        boMItemAndActivityIds,
      },
      onRequestChange: this.onRequestChange,
    })
  }

  async DeleteAdditionalWorkingStep(
    projectId: string,
    boMItemAndActivityIds: BoMItemIdAndActivityId[],
    activityKey?: string,
  ) {
    return await this.PostAsync<void>({
      relativePath: `/${projectId}/workingsteps/delete`,
      data: {
        boMItemAndActivityIds,
      },
      onRequestChange: (req) => {
        this.onRequestChange({
          ...req,
          id:
            WorkingStepsController.WorkingStepsOperations.DELETE_DATA +
            `_${activityKey}`,
        })
      },
    })
  }

  async GetWorkingStepForm(
    projectId: string,
    bomItemId: string,
    bomItemActivityId: string,
    primaryWorkingStep: WorkingStepType,
    isReloading?: boolean,
  ) {
    return await this.GetAsync<AdditionalWorkingStepDto>({
      id:
        WorkingStepsController.WorkingStepsOperations.GET_FORM +
        `${isReloading ? '_reloading' : ''}`,
      relativePath: `/${projectId}/items/${bomItemId}/${bomItemActivityId}/forms`,
      params: {
        primaryWorkingStep,
      },
      onRequestChange: this.onRequestChange,
    })
  }

  async CopyWorkingStepsToOtherParts(
    projectId: string,
    args: CopyWorkingStepArgs,
  ) {
    return await this.PostAsync<void>({
      id: `CopyWorkingStepsToOtherParts`,
      relativePath: `/${projectId}/workingsteps/CopyWorkingStep`,
      data: args,
      onRequestChange: this.onRequestChange,
    })
  }

  async UpdateWorkingStepProductionOrder(
    projectId: string,
    bomItemId: string,
    bomItemActivityId: string,
    moveAfterBomItemActivityId?: string,
  ) {
    const moveAfterPath = moveAfterBomItemActivityId
      ? `/moveAfter/${moveAfterBomItemActivityId}`
      : '/moveAfter'

    return await this.PostAsync<UpdateWorkingStepProductionOrderResponse>({
      id: WorkingStepsController.WorkingStepsOperations.CHANGE_PRODUCTION_ORDER,
      relativePath: `/${projectId}/workingsteps/${bomItemId}/${bomItemActivityId}/productionOrder${moveAfterPath}`,
      onRequestChange: this.onRequestChange,
    })
  }

  public async BatchUpdateWorkingStepsPosition(
    projectId: string,
    args: UpdateWorkingStepsProductionOrderArgs,
  ) {
    return await this.PostAsync<void>({
      id: `BatchUpdateWorkingStepsPosition`,
      relativePath: `/${projectId}/workingsteps/productionOrder`,
      data: args,
      onRequestChange: this.onRequestChange,
    })
  }
}
